import apiClient from '../core';
import type {
  PageLayout,
  CreatePageLayoutRequest,
  UpdatePageLayoutRequest,
  SetPageLayoutRequest,
} from '../../schemas/validation';

export interface LayoutPublishResult {
  message: string;
  layout: PageLayout;
  skipped?: boolean;
  republishedPageIds: string[];
  skippedNeverPublishedPageIds: string[];
}

export const layoutsApi = {
  // Get all layouts for a site
  async list(siteId: string): Promise<{ layouts: PageLayout[] }> {
    const response = await apiClient.get<{ layouts: PageLayout[] }>(`/layouts/site/${siteId}`);
    return response.data;
  },

  // Get a specific layout
  async get(layoutId: string): Promise<PageLayout> {
    const response = await apiClient.get<PageLayout>(`/layouts/${layoutId}`);
    return response.data;
  },

  // Get the default layout for a site
  async getDefault(siteId: string): Promise<{ layout: PageLayout | null }> {
    const response = await apiClient.get<{ layout: PageLayout | null }>(
      `/layouts/site/${siteId}/default`
    );
    return response.data;
  },

  // Create a new layout
  async create(data: CreatePageLayoutRequest): Promise<PageLayout> {
    const response = await apiClient.post<PageLayout>('/layouts', data);
    return response.data;
  },

  // Update a layout
  async update(layoutId: string, data: UpdatePageLayoutRequest): Promise<PageLayout> {
    const response = await apiClient.put<PageLayout>(`/layouts/${layoutId}`, data);
    return response.data;
  },

  // Delete a layout
  async delete(layoutId: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.delete<{
      success: boolean;
      message: string;
    }>(`/layouts/${layoutId}`);
    return response.data;
  },

  // Publish a layout (snapshot + republish dependent pages)
  async publish(layoutId: string): Promise<LayoutPublishResult> {
    const response = await apiClient.put<LayoutPublishResult>(`/layouts/${layoutId}/publish`);
    return response.data;
  },

  // NOTE: layout unpublish is intentionally NOT exposed in the UI.
  // Unpublishing a layout could orphan published pages (they'd lose header/footer).
  // The backend endpoint exists but should only be used with explicit safeguards.

  // Set layout for a page
  async setPageLayout(
    pageId: string,
    data: SetPageLayoutRequest
  ): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.put<{ success: boolean; message: string }>(
      `/layouts/page/${pageId}`,
      data
    );
    return response.data;
  },
};
